import { FC, useState } from "react";
import { OnboardingStatusController } from "../../onboardingstatus/OnboardingStatusController";
import { ExtendedShareholderDTO } from "../../types/Shareholder";
import { observer } from "mobx-react";
import { Alert } from "../../components-v2/Alert";
import { isFailure } from "../../common/common";
import { Button } from "../../components-v2/Button";
import { Box, VBox } from "../../components-v2/Box";
import { TMessageKey } from "../../i18n/types.gen";

import { useTranslation } from "../../stores/RootStoreContext";

export const ShareholderOnboardingStatus: FC<{
  controller: OnboardingStatusController;
  activeShareholders: ExtendedShareholderDTO[];
  hasInvalidShareholders: boolean;
}> = observer(({ controller, activeShareholders, hasInvalidShareholders }) => {
  const { t } = useTranslation();
  const [showCannotConfirmShareholdersMessage, setShowCannotConfirmShareholdersMessage] =
    useState(false);
  return (
    <>
      {controller.missingShareholders && (
        <Box grow>
          <Alert
            grow
            type="error"
            message={t("onboarding.step.error.shareholders.shareholder.missing")}
          />
        </Box>
      )}
      {isFailure(controller.shareholdersOnboardingComplete) && (
        <Box grow>
          <Alert grow type="error" message={t("onboarding.step.error.confirm")} />
        </Box>
      )}
      {controller.serverValidationErrors.length > 0 && (
        <Box grow>
          <Alert
            grow
            type="error"
            message={
              <div>
                {controller.serverValidationErrors.map((it) => (
                  <div key={it.validationError}>
                    {t(it.validationError as TMessageKey, { param: it.context })}
                  </div>
                ))}
              </div>
            }
          />
        </Box>
      )}
      {controller.renderIncompleteShareholders && activeShareholders.length > 0 && (
        <Box>
          <Alert
            type="info"
            message={
              <VBox>
                <div>{t("onboarding.step.why.shareholders.header")}</div>
                <Box>
                  <Button
                    variant={"secondary"}
                    size={"sm"}
                    label={t("onboarding.action.confirmShareholders")}
                    onClick={() => {
                      if (!hasInvalidShareholders) {
                        controller.onboardShareholders(activeShareholders);
                      } else {
                        setShowCannotConfirmShareholdersMessage(true);
                      }
                    }}
                    disabled={controller.isSaveInProgress}
                    loading={controller.isSaveInProgress}
                  />
                </Box>
                {showCannotConfirmShareholdersMessage && (
                  <div className={"subtle"}>{t("onboarding.step.error.shareholders.tooltip")}</div>
                )}
              </VBox>
            }
          />
        </Box>
      )}
      {controller.renderSuccessfulShareholdersOnboarding && (
        <Box grow>
          <Alert
            grow
            type={"success"}
            message={<Box grow>{t("onboardingstatus.success.shareholders")}</Box>}
          />
        </Box>
      )}
    </>
  );
});
